import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchorType,
  IExperience,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  IReportActor,
  IReportTasks,
  IReportTasksDetailedCohortStats,
  TaskStatusCounts
} from '@dadohr/shared/domainTypes/reports';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { capitalize, compact, flatten } from 'lodash';
import { ExternalLink } from 'lucide-react';
import moment from 'moment';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { WithShape } from '../../../../components/Charts/Util';
import { DataTable } from '../../../../components/DataTable';
import { formatNumber } from '../../../../components/Number';
import { TooltipWhite } from '../../../../components/Tooltip';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useHover } from '../../../../hooks/useHover';
import { useHoverDurationTracking } from '../../../../hooks/useHoverDurationTracking';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer } from '../../../../layout/Flex';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useExperienceInstancesByIds } from '../../../../services/experiences/instance';
import { getTaskTemplateById } from '../../../../services/experiences/template';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { toMoment } from '../../../../services/time';
import { useTheme } from '../../../../themes';
import { useAnchorFields } from '../../../Experiences/components/Anchors';
import { CardItemDialog } from '../../../Experiences/components/CalendarV2/CardItemDialog';
import { TimelineItemExecutionTableLazy } from '../../../Experiences/components/CalendarV2/DialogTable';
import { OnHover } from '../../../Experiences/components/ExecutionReportTable/Tasks';
import { CAL_V2_AGGREGATION_DATE_FORMAT } from '../../../Experiences/pages/Details/CalendarV2/aggregate';
import { ParticipantDialog } from '../../../Experiences/pages/Details/ParticipantDialog';
import { ActorLegend } from '../../../Experiences/pages/Details/ParticipantDialogComponents/ActorLegend';
import { TaskTemplateReadOnlyDialog } from '../../../Experiences/pages/Details/TaskTimeline';
import { getItemDisplayName, getItemDisplayPair } from '../../../Experiences/helpers/adminName';
import { ActorSelector } from '../ActorSelector';
import { ChartTitle } from '../ChartTitle';
import { getHeatmapColor } from './colors';

const NBSP = String.fromCharCode(160);

const COLORS = {
  completedOnTime: (colors: Theme['custom']['colors']) => colors.green.w400,
  completedLate: (colors: Theme['custom']['colors']) => colors.green.w500
};

const ANCHOR_COLUMN_WIDTH = 11;
const COHORT_COLUMN_WIDTH = 5;
const STICKY_COLUMN_PADDING_END = 4;

type BrickProps = { background?: string; foreground?: string };

const AnchorCell = styled.div`
  width: ${(p) => p.theme.spacing(ANCHOR_COLUMN_WIDTH)}px;
`;

const CohortCell = styled.div`
  width: ${(p) => p.theme.spacing(COHORT_COLUMN_WIDTH)}px;
`;

const Brick = styled.div`
  display: block;
  box-sizing: content-box;
  padding-inline: ${(p) => p.theme.spacing(3)}px;
  padding-block: ${(p) => p.theme.spacing(1.5)}px;
  min-width: 4ch;
  height: 1em;
  line-height: 1em;
  background-color: ${(p: BrickProps) => p.background || '#fafafa'};
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  color: ${(p: BrickProps) => p.foreground};
  text-align: center;

  &:hover {
    box-shadow: inset 0 0 0 ${(p) => p.theme.spacing(0.25)}px
      rgba(8, 18, 26, 20%);
    cursor: pointer;
  }
`;

const TaskDidNotApplyBrick = () => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  return (
    <TooltipWhite
      title={t('This task didn’t apply for this cohort').toString()}
    >
      <Brick background={theme.custom.colors.gray.w200} />
    </TooltipWhite>
  );
};

const CohortSizeCell = styled(CohortCell)`
  color: ${(p) => p.theme.custom.colors.gray.w500};
`;

const CompletionTable = styled(DataTable)`
  width: auto !important;
  border-collapse: collapse;
  white-space: nowrap;

  thead :is(td, th) {
    height: ${(p) => p.theme.spacing(20)}px;
    position: sticky;
    top: 0;
    background-color: white;
    color: ${(p) => p.theme.custom.colors.gray.w500};
    font-size: ${(p) => p.theme.custom.fontSize.s}px;
    font-weight: ${(p) => p.theme.typography.fontWeightRegular};
    text-align: start;
    vertical-align: bottom;
    white-space: normal;
  }

  thead :is(td, th):is(:first-of-type, :nth-child(2)) {
    font-weight: ${(p) => p.theme.typography.fontWeightBold};
  }

  td,
  th {
    border: 0;
    text-align: start;
  }

  td:first-of-type,
  th:first-of-type {
    padding-inline-end: ${(p) => p.theme.spacing(STICKY_COLUMN_PADDING_END)}px;
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: white;
    white-space: normal;
  }

  td:nth-child(2),
  th:nth-child(2) {
    padding-inline-end: ${(p) => p.theme.spacing(STICKY_COLUMN_PADDING_END)}px;
    position: sticky;
    left: ${
      (p) =>
        p.theme.spacing(ANCHOR_COLUMN_WIDTH) +
        p.theme.spacing(STICKY_COLUMN_PADDING_END) +
        1 // Anchor column left padding
    }px;
    z-index: 1;
    background-color: white;
    text-align: end;
  }
`;

const Container = styled.div`
  margin-block-start: ${(p) => p.theme.spacing(3)}px;
`;

const TableContainer = styled.div`
  overflow: auto;
`;

const TaskHeading = styled.div`
  width: 0;
  overflow: visible;

  /* transform: translateX(${(p) => p.theme.spacing(2)}px) rotate(-45deg);
  transform-origin: 0% 100%; */

  /* basically guessing values here */
  transform: translateX(${(p) => p.theme.spacing(2)}px) rotate(-45deg)
    translateY(${(p) => p.theme.spacing(1.5)}px);
  transform-origin: 0% -50%;
`;

const TaskTitle = styled.div`
  display: block;
  width: ${(p) => p.theme.spacing(20)}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const DialogHeaderTitle = ({
  color,
  actor,
  title,
  taskStart,
  tpl,
  taskId
}: {
  actor: string;
  color: string;
  title: string;
  taskStart?: firebase.firestore.Timestamp;
  tpl: Doc<IExperienceTemplate>;
  taskId: string;
}) => {
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const ref = useRef<HTMLDivElement>(null);
  const hovered = useHover(ref);
  const { ROUTES, goTo } = useRoutes();

  const subHeadingDateLabel = (taskStart: Timestamp) => {
    const format = toMoment(taskStart).format(CAL_V2_AGGREGATION_DATE_FORMAT);
    const diff = moment(format).diff(
      moment().format(CAL_V2_AGGREGATION_DATE_FORMAT),
      'days'
    );
    return diff < 0 ? 'Started on: ' : 'Will start on: ';
  };

  return (
    <FlexContainer fullWidth direction="column" alignItems={'flex-start'}>
      <FlexContainer
        spacing={1}
        justifyContent="flex-start"
        alignItems="center"
        fullWidth
      >
        <ActorLegend type={'task'} color={color}>
          <Typography variant="subtitle2">{actor}</Typography>
        </ActorLegend>
        <OnHover
          ref={ref}
          onClick={() =>
            goTo(
              ROUTES.experiences.details.general.url(tpl.id, {
                type: 'task',
                taskId: taskId
              })
            )
          }
        >
          <Typography variant="h4">{title}</Typography>
          {hovered && <ExternalLink size={16} />}
        </OnHover>
      </FlexContainer>
      {taskStart && (
        <Typography variant="caption" color="textSecondary">
          {subHeadingDateLabel(taskStart)}
          {toMoment(taskStart)
            .tz(tpl.data.tz)
            .format(`${org.dateFormats.MMDDYYYY}  HH:mm`)}{' '}
          {toMoment(taskStart).tz(tpl.data.tz).format('z')}
        </Typography>
      )}
    </FlexContainer>
  );
};
const CompletionBrick = ({
  dates,
  data,
  openDialog
}: {
  dates: { start: Timestamp; end: Timestamp };
  data: TaskStatusCounts;
  openDialog: () => void;
}) => {
  const {
    custom: { colors }
  } = useTheme();
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const now = new Date();

  if (now < dates.end.toDate()) {
    return (
      <TooltipWhite
        title={
          <>
            {t('Deadline')}:{' '}
            {toMoment(dates.end).format(org.dateFormats.MMDDYYYY)}
            <br />
            {t('Completion will be calculated after deadline.')}
          </>
        }
      >
        <Brick />
      </TooltipWhite>
    );
  }

  // Computing total count (and thus not using data.total) while we discover whether to include skipped tasks in the total
  const total = data.completedLate + data.completedOnTime + data.overdue;

  if (total === 0) {
    return <TaskDidNotApplyBrick />;
  }

  const completed = data.completedOnTime + data.completedLate;
  const value = completed / total;

  const { foreground, background } = getHeatmapColor(value);

  return (
    <TooltipWhite
      title={
        <div>
          <FlexContainer spacing={2} justifyContent="space-between">
            <WithShape color="lightgray" shape="square" narrow>
              {capitalize(t('total'))}{' '}
            </WithShape>
            {total}
          </FlexContainer>
          <FlexContainer spacing={2} justifyContent="space-between">
            <WithShape
              color={COLORS.completedOnTime(colors)}
              shape="square"
              narrow
            >
              {capitalize(t('done on time'))}{' '}
            </WithShape>
            {data.completedOnTime}
          </FlexContainer>
          <FlexContainer spacing={2} justifyContent="space-between">
            <WithShape
              color={COLORS.completedLate(colors)}
              shape="square"
              narrow
            >
              {capitalize(t('done late'))}{' '}
            </WithShape>
            {data.completedLate}
          </FlexContainer>
        </div>
      }
    >
      <Brick
        background={background}
        foreground={foreground}
        onClick={openDialog}
      >
        {formatNumber({ n: value, format: 'percent' })}
      </Brick>
    </TooltipWhite>
  );
};

const CompletionBrickWithDialog = ({
  task,
  tpl,
  instanceIds,
  actor,
  color,
  setDialogInstance
}: {
  task: {
    templateId: string;
    taskTemplateId: string;
    title: string;
    actorKey: string;
    counts: TaskStatusCounts;
    normalTaskDates: {
      start: firebase.firestore.Timestamp;
      end: firebase.firestore.Timestamp;
    };
  };
  tpl: Doc<IExperienceTemplate>;
  instanceIds: string[];
  actor: { key: string; label: string };
  color: string;
  setDialogInstance: (inst: Doc<IExperience>) => void;
}) => {
  const innerDialog = useDialogState(false);
  return (
    <>
      <CompletionBrick
        dates={task.normalTaskDates}
        data={task.counts}
        openDialog={innerDialog.openDialog}
      />
      <CardItemDialog
        open={innerDialog.dialogOpen}
        onClose={innerDialog.closeDialog}
        headerLeft={
          <DialogHeaderTitle
            actor={actor.label}
            title={getItemDisplayName({
              ...task,
              title: task.title.replace(/\{\{\s*experience\.title\s*\}\}/g, tpl.data.title)
            })}
            color={color}
            taskStart={task.normalTaskDates.start}
            tpl={tpl}
            taskId={task.taskTemplateId}
          />
        }
        dialogTable={
          <TimelineItemExecutionTableLazy
            type="task"
            instanceIds={instanceIds}
            tplId={tpl.id}
            actor={actor}
            itemTplId={task.taskTemplateId}
            onShowInstanceDetails={setDialogInstance}
          />
        }
      />
    </>
  );
};

const HeatmapTaskHeader = ({
  tpl,
  actors,
  task,
  instanceIds
}: {
  tpl: Doc<IExperienceTemplate>;
  actors: IReportActor[];
  task: {
    title: string;
    taskTemplateId: string;
    actorKey: string;
  };
  instanceIds: string[];
}) => {
  const actor = actors.find((a) => a.key === task.actorKey);
  const color = actor?.color || '#000';
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const { org } = useCurrentUser();
  const anchorFields = useAnchorFields(org.customUserFields);
  const taskTpl = getTaskTemplateById(tpl, task.taskTemplateId);
  const [instances] = useMappedLoadingValue(
    useExperienceInstancesByIds(instanceIds),
    compact
  );

  const resolveToken = (s: string) =>
    s.replace(/\{\{\s*experience\.title\s*\}\}/g, tpl.data.title);
  const resolvedTask = {
    ...task,
    title: resolveToken(task.title),
    adminName: taskTpl?.adminName != null
      ? resolveToken(taskTpl.adminName)
      : taskTpl?.adminName
  };
  const { primary, secondary } = getItemDisplayPair(resolvedTask);
  return (
    <>
      <Tooltip
        placement="bottom-start"
        title={
          secondary ? (
            <div>
              <div>{primary}</div>
              <em>"{secondary}"</em>
            </div>
          ) : primary
        }
      >
        <TaskHeading>
          <WithShape key="participant" color={color}>
            <TaskTitle onClick={openDialog}>{getItemDisplayName(resolvedTask)}</TaskTitle>
          </WithShape>
        </TaskHeading>
      </Tooltip>
      {taskTpl && (
        <TaskTemplateReadOnlyDialog
          open={dialogOpen}
          onClose={closeDialog}
          tpl={taskTpl}
          expTpl={tpl}
          anchorFields={anchorFields}
          initialModalTab="people"
        />
      )}
    </>
  );
};

const Heatmap = ({
  actors,
  cohorts,
  org,
  t,
  tasks,
  tpl,
  setDialogInstance
}: {
  actors: IReportActor[];
  cohorts: IReportTasksDetailedCohortStats[];
  org: IOrganization;
  t: UnsafeTFunction;
  tasks: {
    title: string;
    templateId: string;
    taskTemplateId: string;
    actorKey: string;
  }[];
  tpl: Doc<IExperienceTemplate>;
  setDialogInstance: (inst: Doc<IExperience>) => void;
}) => {
  const hoverTrackingProps = useHoverDurationTracking({
    name: 'Completion Per Cohort Chart Hovered',
    explanation:
      'Admin user hovered over the heat map showing completion rate per task and per cohort'
  });

  const anchorFields = useAnchorFields(org.customUserFields);
  const { anchor } = tpl.data;

  const dataTableHeaders = useMemo(() => {
    const anchorField =
      anchor.type === ExperienceAnchorType.EMPLOYEE_DATE ? anchor.field : '';

    const anchorLabel = anchorField
      ? capitalize(
          anchorFields.find((f) => f.field === anchorField)?.shortLabel(t) ||
            anchorField
        )
      : t('Experience Start');
    return {
      anchor: (
        <AnchorCell>
          {anchorLabel === t('First day at work')
            ? t(`First week at${NBSP}work`)
            : anchorLabel === t(`Last day at work`)
            ? t(`Last week${NBSP}at work`)
            : anchorLabel === t('Experience Start')
            ? t(`Experience${NBSP}start week`)
            : anchorLabel}
        </AnchorCell>
      ),
      cohortSize: <CohortCell>{t('Cohort size')}</CohortCell>,
      ...Object.fromEntries(
        tasks.map((t) => {
          const instanceIds = flatten(
            cohorts.map((cohort) => {
              const task = cohort.tasksByTemplateId[t.taskTemplateId];
              return task ? cohort.instanceIds : [];
            })
          );
          return [
            t.taskTemplateId,
            <HeatmapTaskHeader
              tpl={tpl}
              task={t}
              actors={actors}
              instanceIds={instanceIds}
            />
          ];
        })
      )
    };
  }, [actors, tasks, anchorFields, t, anchor, cohorts, tpl]);

  const dataTableData = useMemo(
    () =>
      cohorts.map<{ [key: string]: ReactNode }>((cohort) => ({
        anchor: (
          <AnchorCell>
            {toMoment(cohort.anchor.date)
              .tz(cohort.anchor.tz)
              .format(org.dateFormats.MMDDYYYY)}
          </AnchorCell>
        ),
        cohortSize: (
          <CohortSizeCell>{formatNumber({ n: cohort.size })}</CohortSizeCell>
        ),

        ...Object.fromEntries(
          tasks.map((taskTpl) => {
            const task = cohort.tasksByTemplateId[taskTpl.taskTemplateId];

            if (!task) {
              return [taskTpl.taskTemplateId, <TaskDidNotApplyBrick />];
            }

            const actor = actors.find(
              (actor) => actor.key === task.actorKey
            ) || { label: '', color: '', key: '' };

            return [
              taskTpl.taskTemplateId,
              <CompletionBrickWithDialog
                task={task}
                instanceIds={cohort.instanceIds}
                actor={{ key: actor.key, label: actor.label }}
                color={actor.color}
                tpl={tpl}
                setDialogInstance={setDialogInstance}
              />
            ];
          })
        )
      })),
    [cohorts, org.dateFormats.MMDDYYYY, tasks, actors, tpl, setDialogInstance]
  );

  return (
    <TableContainer {...hoverTrackingProps}>
      <CompletionTable
        caption="Completion per cohort"
        data={dataTableData}
        headers={dataTableHeaders}
      />
    </TableContainer>
  );
};

export const ReportExperienceCompletionHeatmap = ({
  actors,
  data,
  tpl
}: {
  actors: IReportActor[];
  data: IReportTasks['data']['byCohort'];
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const [dialogInstance, setDialogInstance] = useState<Doc<IExperience> | null>(
    null
  );

  const [state, setState] = useState<{
    actors: string[];
    sortDir: 'asc' | 'desc';
  }>({ actors: actors.map((a) => a.key), sortDir: 'desc' });

  if (data.cohortsByWeek.length === 0) {
    return null;
  }

  const tasksOfSelectedParticipants = data.tasks.filter((t) =>
    state.actors.includes(t.actorKey)
  );

  return (
    <>
      <ChartTitle
        title={t('Completion per cohort')}
        tooltipText={`${t(
          'The rate at which tasks were completed for each cohort, excluding Upcoming and Skipped tasks'
        )}`}
      />
      <Typography variant="h4">{}</Typography>
      <Container>
        <ActorSelector
          actors={actors}
          selected={state.actors}
          setSelected={(nextActors) =>
            setState((s) => ({ ...s, actors: nextActors }))
          }
          trackingKey="Completion per cohort"
        />

        {tasksOfSelectedParticipants.length > 0 ? (
          <Heatmap
            actors={actors}
            cohorts={data.cohortsByWeek}
            org={org}
            t={t}
            tasks={tasksOfSelectedParticipants}
            tpl={tpl}
            setDialogInstance={setDialogInstance}
          />
        ) : (
          <Centered height={200}>
            <Typography>{t('No tasks match these criteria.')}</Typography>
          </Centered>
        )}
      </Container>
      {dialogInstance && (
        <ParticipantDialog
          open={true}
          tpl={tpl}
          d={dialogInstance}
          onClose={() => setDialogInstance(null)}
        />
      )}
    </>
  );
};
